import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography,
} from "@mui/material";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import SendIcon from "@mui/icons-material/Send";
import { emailCampaignsApi } from "../components/massEmail/api/emailCampaignsApi";

// ─── Harare time helpers (mirrored from CreateEmailCampaignModal) ────────────
const HARARE_OFFSET_STR = "+02:00";
const HARARE_OFFSET_MS  = 2 * 60 * 60 * 1000;
const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

function toHarareDatetimeValue(utcDate) {
  const h = new Date(utcDate.getTime() + HARARE_OFFSET_MS);
  return `${h.getUTCFullYear()}-${String(h.getUTCMonth()+1).padStart(2,"0")}-${String(h.getUTCDate()).padStart(2,"0")}T${String(h.getUTCHours()).padStart(2,"0")}:${String(h.getUTCMinutes()).padStart(2,"0")}`;
}

function parseHarareDatetimeInput(localStr) {
  if (!localStr) return null;
  const d = new Date(localStr.slice(0, 16) + ":00" + HARARE_OFFSET_STR);
  return Number.isNaN(d.getTime()) ? null : d;
}

function toHarareOffsetIso(localStr) {
  if (!localStr) return null;
  return localStr.slice(0, 16) + ":00" + HARARE_OFFSET_STR;
}

function fmtHarare(raw) {
  if (!raw) return "—";
  try {
    const dt = new Date(raw);
    if (Number.isNaN(dt.getTime())) return "—";
    const h = new Date(dt.getTime() + HARARE_OFFSET_MS);
    return `${String(h.getUTCDate()).padStart(2,"0")} ${MONTHS[h.getUTCMonth()]} ${h.getUTCFullYear()}, ${String(h.getUTCHours()).padStart(2,"0")}:${String(h.getUTCMinutes()).padStart(2,"0")}`;
  } catch { return "—"; }
}

// Build a datetime-local value for the suggested send date at 08:00 Harare
function suggestedSendDatetimeValue(dateStr) {
  if (!dateStr) return toHarareDatetimeValue(new Date(Date.now() + 60 * 60 * 1000));
  return `${dateStr}T08:00`;
}

// ─── Component ───────────────────────────────────────────────────────────────

export default function SeasonalCampaignModal({ open, onClose, occasion, onSuccess }) {
  const [step, setStep]               = useState(0);
  const [customBrief, setCustomBrief] = useState("");
  const [generating, setGenerating]   = useState(false);
  const [draft, setDraft]             = useState(null);
  const [genError, setGenError]       = useState("");

  const [scheduleLocal, setScheduleLocal] = useState("");
  const [launching, setLaunching]         = useState(false);
  const [launchError, setLaunchError]     = useState("");

  // Reset whenever the dialog opens with a new occasion
  useEffect(() => {
    if (open && occasion) {
      setStep(0);
      setCustomBrief("");
      setDraft(null);
      setGenError("");
      setLaunchError("");
      setLaunching(false);
      setScheduleLocal(suggestedSendDatetimeValue(occasion.suggested_send_date));
    }
  }, [open, occasion]);

  const handleGenerate = async () => {
    setGenerating(true);
    setGenError("");
    try {
      const result = await emailCampaignsApi.seasonalDraft({
        occasion_key: occasion.key,
        custom_brief: customBrief,
      });
      setDraft(result);
    } catch (err) {
      setGenError(err?.response?.data?.message || "Gemini generation failed. Please try again.");
    } finally {
      setGenerating(false);
    }
  };

  const handleLaunch = async () => {
    setLaunching(true);
    setLaunchError("");
    try {
      const scheduleDate = parseHarareDatetimeInput(scheduleLocal);
      const isScheduled  = scheduleDate && scheduleDate > new Date();

      await emailCampaignsApi.create({
        scope:                "filter_all",
        name:                 `${occasion.name} Campaign — ${occasion.date}`,
        subject:              draft.subject,
        body_html:            draft.body_html,
        personalisation_mode: "none",
        filters:              {},
        ...(isScheduled ? { schedule_at: toHarareOffsetIso(scheduleLocal), tz: "Africa/Harare" } : {}),
      });

      onSuccess?.();
    } catch (err) {
      setLaunchError(err?.response?.data?.message || "Failed to launch campaign. Please try again.");
      setLaunching(false);
    }
  };

  if (!occasion) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Typography variant="h4" sx={{ lineHeight: 1 }}>{occasion.emoji}</Typography>
          <Box>
            <Typography variant="h6" fontWeight={700}>{occasion.name} Campaign</Typography>
            <Typography variant="caption" color="text.secondary">
              {new Date(occasion.date + "T00:00:00").toLocaleDateString("en-GB", {
                weekday: "long", day: "numeric", month: "long", year: "numeric",
              })}
              {" · "}
              {occasion.days_until > 0 ? `${occasion.days_until} days away` : occasion.days_until === 0 ? "Today!" : `${Math.abs(occasion.days_until)} days ago`}
            </Typography>
          </Box>
        </Box>
      </DialogTitle>

      <Stepper activeStep={step} sx={{ px: 3, py: 2 }}>
        <Step><StepLabel>Generate Content</StepLabel></Step>
        <Step><StepLabel>Review & Send</StepLabel></Step>
      </Stepper>

      <DialogContent dividers sx={{ minHeight: 360 }}>
        {step === 0 && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Typography variant="body2" color="text.secondary">
              {occasion.description}
            </Typography>

            <TextField
              label="Optional: customise the campaign focus"
              placeholder={occasion.suggested_brief}
              multiline
              rows={3}
              fullWidth
              value={customBrief}
              onChange={e => setCustomBrief(e.target.value)}
              helperText="Leave blank to use the suggested brief. Add specific packages, destinations, or offers to include."
            />

            {genError && <Alert severity="error">{genError}</Alert>}

            {!draft && (
              <Button
                variant="contained"
                startIcon={generating ? <CircularProgress size={18} color="inherit" /> : <AutoAwesomeIcon />}
                onClick={handleGenerate}
                disabled={generating}
                sx={{ alignSelf: "flex-start", borderRadius: "10px", textTransform: "none", fontWeight: 700 }}
              >
                {generating ? "Generating with Gemini…" : "Generate with Gemini"}
              </Button>
            )}

            {draft && (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                <Alert severity="success" sx={{ borderRadius: "10px" }}>
                  Content generated! Review below or regenerate with a different brief.
                </Alert>

                <Box sx={{ p: 2, bgcolor: "action.hover", borderRadius: "10px" }}>
                  <Typography variant="caption" color="text.secondary" fontWeight={700}>SUBJECT</Typography>
                  <Typography variant="body1" fontWeight={600} sx={{ mt: 0.5 }}>{draft.subject}</Typography>
                </Box>

                <Box sx={{ p: 2, bgcolor: "action.hover", borderRadius: "10px", maxHeight: 260, overflowY: "auto" }}>
                  <Typography variant="caption" color="text.secondary" fontWeight={700}>EMAIL PREVIEW</Typography>
                  <Box
                    sx={{ mt: 1, fontSize: "0.85rem", lineHeight: 1.7 }}
                    dangerouslySetInnerHTML={{ __html: draft.body_html }}
                  />
                </Box>

                <Button
                  variant="outlined"
                  size="small"
                  startIcon={generating ? <CircularProgress size={16} /> : <AutoAwesomeIcon />}
                  onClick={handleGenerate}
                  disabled={generating}
                  sx={{ alignSelf: "flex-start", textTransform: "none" }}
                >
                  {generating ? "Regenerating…" : "Regenerate"}
                </Button>
              </Box>
            )}
          </Box>
        )}

        {step === 1 && draft && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
            <Box sx={{ p: 2, bgcolor: "action.hover", borderRadius: "10px" }}>
              <Typography variant="caption" color="text.secondary" fontWeight={700}>SUBJECT</Typography>
              <Typography variant="body1" fontWeight={600} sx={{ mt: 0.5 }}>{draft.subject}</Typography>
            </Box>

            <Divider />

            <Box>
              <Typography variant="subtitle2" fontWeight={700} gutterBottom>Audience</Typography>
              <Typography variant="body2" color="text.secondary">
                All eligible email contacts · delivery guards apply (contacts emailed recently will be skipped)
              </Typography>
            </Box>

            <Divider />

            <Box>
              <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                Schedule
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                Pre-set to 7 days before <strong>{occasion.name}</strong> so recipients have time to plan and book.
                You can change this.
              </Typography>
              <TextField
                type="datetime-local"
                label="Send at (Harare time)"
                value={scheduleLocal}
                onChange={e => setScheduleLocal(e.target.value)}
                InputLabelProps={{ shrink: true }}
                inputProps={{ min: toHarareDatetimeValue(new Date(Date.now() + 2 * 60 * 1000)) }}
                helperText={
                  scheduleLocal && parseHarareDatetimeInput(scheduleLocal)
                    ? `Sends at ${fmtHarare(toHarareOffsetIso(scheduleLocal))} Harare time`
                    : "Select a future date and time"
                }
                fullWidth
              />
            </Box>

            {launchError && <Alert severity="error">{launchError}</Alert>}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} color="inherit" disabled={launching}>Cancel</Button>

        {step === 0 && (
          <Button
            variant="contained"
            onClick={() => setStep(1)}
            disabled={!draft}
            sx={{ textTransform: "none", fontWeight: 700 }}
          >
            Next: Review & Send
          </Button>
        )}

        {step === 1 && (
          <>
            <Button onClick={() => setStep(0)} color="inherit" disabled={launching}>Back</Button>
            <Button
              variant="contained"
              color="success"
              startIcon={launching ? <CircularProgress size={18} color="inherit" /> : <SendIcon />}
              onClick={handleLaunch}
              disabled={launching}
              sx={{ textTransform: "none", fontWeight: 700 }}
            >
              {launching ? "Launching…" : "Launch Seasonal Campaign"}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}
